import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Spinner,
  Textarea,
  Toaster,
  toast,
} from "@itoresearch/design-system";
import { AppWindowIcon } from "lucide-react";

import { useUser } from "../../hooks/use-user";
import { useApps, useCreateApp } from "../../hooks/use-apps";
import { ApiError } from "../../lib/api-client";
import { slugify } from "../../lib/apps";

export const Route = createFileRoute("/apps/")({
  component: AppsPage,
});

function AppsPage() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Checking session…
      </div>
    );
  if (!user) return null;

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Apps</h1>
          <p className="text-muted-foreground">
            Push code to a Git repository and run it on Ito compute.
          </p>
        </div>
        <CreateAppDialog />
      </div>
      <AppList />
    </div>
  );
}

function AppList() {
  const apps = useApps();

  if (apps.isPending)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Loading apps…
      </div>
    );

  if (apps.isError)
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          <p className="text-sm">
            Failed to load apps:{" "}
            {apps.error instanceof Error
              ? apps.error.message
              : "unexpected error"}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void apps.refetch()}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );

  if (apps.data.data.length === 0)
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <AppWindowIcon />
          </EmptyMedia>
          <EmptyTitle>No apps yet</EmptyTitle>
          <EmptyDescription>
            Create an app to get a Git repository and compute on Ito.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <CreateAppDialog />
        </EmptyContent>
      </Empty>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {apps.data.data.map((app) => (
        <Link
          key={app.appId}
          to="/apps/$appId"
          params={{ appId: app.appId }}
          className="group"
        >
          <Card className="h-full transition-colors group-hover:border-foreground/20">
            <CardContent className="space-y-2 pt-6">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">{app.name}</p>
                <Badge
                  variant={app.status === "ACTIVE" ? "default" : "secondary"}
                >
                  {app.status.toLowerCase()}
                </Badge>
              </div>
              <p className="font-mono text-xs text-muted-foreground">
                {app.slug}
              </p>
              {app.description && (
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {app.description}
                </p>
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function CreateAppDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateApp();

  // When the user hasn't typed a slug, show what the API will derive.
  const slugPreview = slug || slugify(name);
  const slugDirty = slug.length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name,
        description: description.trim() || undefined,
        slug: slugDirty ? slug : undefined,
      },
      {
        onSuccess: (app) => {
          setOpen(false);
          setName("");
          setSlug("");
          setDescription("");
          toast.add({
            title: "App created",
            type: "success",
            description: `${app.name} (${app.slug}) is ready.`,
          });
        },
        onError: (error) => {
          const message =
            error instanceof ApiError && error.status === 409
              ? "That slug is already taken — pick another one."
              : error instanceof ApiError
                ? error.message
                : "Unexpected error creating the app.";
          toast.add({
            title: "Could not create app",
            type: "error",
            description: message,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button />}>
        <AppWindowIcon data-slot="icon" /> New app
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Create an app</DialogTitle>
            <DialogDescription>
              Each app gets its own Git repository on Ito compute.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="gap-4 py-2">
            <Field>
              <FieldLabel htmlFor="app-name">Name</FieldLabel>
              <Input
                id="app-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Volatility Scanner"
                required
                maxLength={200}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="app-slug">Slug</FieldLabel>
              <Input
                id="app-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="vol-scanner"
                pattern="[a-z0-9]+(-[a-z0-9]+)*"
                title="Lowercase letters, digits, and hyphens"
                maxLength={63}
              />
              <FieldDescription>
                {slugPreview
                  ? `Will be “${slugPreview}” — this becomes the app's public hostname and cannot be changed later.`
                  : "Lowercase letters, digits, and hyphens."}
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="app-description">Description</FieldLabel>
              <Textarea
                id="app-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this app do?"
                rows={3}
                maxLength={2000}
              />
            </Field>
          </FieldGroup>
          {create.isError && (
            <FieldError
              errors={[
                {
                  message:
                    create.error instanceof ApiError
                      ? create.error.message
                      : "Could not create the app.",
                },
              ]}
            />
          )}
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>
              Cancel
            </DialogClose>
            <Button
              type="submit"
              disabled={create.isPending || name.trim().length === 0}
            >
              {create.isPending ? <Spinner /> : null} Create app
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
